/**
 * H-88 (Pivot): Client-side cache for the `getClientConfig` Cloud Function.
 *
 * The CF returns the Google Maps JS API key (GRC-05) and runtime feature
 * flags. Both `useGoogleMapsScript` and `useFeatureFlags` consume this cache
 * so we make a single network call per session (the CF is rate-limited to 5
 * fetches per 15 min per user — see clientConfig.ts on the server).
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface ClientConfigFeatures {
  /** H-88: When false, Discovery routes/nav/prefetch are disabled. Default false. */
  discoveryEnabled: boolean;
  /** H-90: When false, ComplementarySelector + complementary pipeline phase are disabled. Default false. */
  complementaryEnabled: boolean;
  /** M-99: When false, Ticketmaster event search is skipped server-side. Default false. */
  ticketmasterEnabled: boolean;
  /** H-147: When false, H-140–H-146 progressive disclosure UI is hidden; v1 flow is shown. Default false. */
  progressiveDisclosureEnabled: boolean;
}

export interface ClientConfigResponse {
  googleMapsApiKey: string;
  /** Optional for backward-compat with older deploys that haven't shipped H-88. */
  features?: ClientConfigFeatures;
}

let cached: ClientConfigResponse | null = null;
let inFlight: Promise<ClientConfigResponse> | null = null;

export async function fetchClientConfig(): Promise<ClientConfigResponse> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const fn = httpsCallable<unknown, ClientConfigResponse>(functions, 'getClientConfig');
    const result = await fn({});
    cached = result.data;
    return cached;
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/** Test-only helper — clears the module-level cache between tests. */
export function _resetClientConfigCacheForTest(): void {
  cached = null;
  inFlight = null;
}
